import React, { useEffect, useState } from "react";
import { CampaignStatusItem } from "../../api/fetchCampaignStatus/types";
import { CampaignIterationStatusData } from "@/modules/administration/features/Stochastic/CampaignStatus/api/fetchCampaignIterationsStatusData/types";
import { fetchCampaignIterationsStatus } from "@/modules/administration/features/Stochastic/CampaignStatus/api/fetchCampaignIterationsStatusData/fetchCampaignIterationsStatusDataApi";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { format } from "date-fns";
import { Users } from "lucide-react";
import { formatCurrency } from "@/utils/formatCurrency";

interface CampaignStatusExpandedRowProps {
  campaign: CampaignStatusItem;
}

const CampaignStatusExpandedRow: React.FC<CampaignStatusExpandedRowProps> = ({
  campaign,
}) => {
  const [iterationsData, setIterationsData] = useState<
    CampaignIterationStatusData[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(false);

  const getStatusColor = (statusName: string): string => {
    const normalizedStatus = statusName.toLowerCase();
    switch (normalizedStatus) {
      // Campaign Iteration Statuses
      case "active":
        return "bg-green-100 text-green-700";
      case "completed":
        return "bg-blue-100 text-blue-700";
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      case "paused":
        return "bg-yellow-100 text-yellow-700";
      case "archived":
        return "bg-gray-300 text-gray-600";

      // Batch/Week Statuses
      case "new":
        return "bg-green-100 text-green-700";
      case "processed":
        return "bg-blue-100 text-blue-700";
      case "sent":
        return "bg-green-100 text-green-700";
      case "invoiced":
        return "bg-purple-100 text-purple-700";
      case "complete":
        return "bg-blue-100 text-blue-700";

      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  useEffect(() => {
    const loadCampaignIterations = async () => {
      try {
        setLoading(true);
        setError(null);
        const iterationsResponse = await fetchCampaignIterationsStatus(
          campaign.id,
        );
        console.log("Iterations Response:", iterationsResponse);
        console.log("Iterations Data:", iterationsResponse.data);
        setIterationsData(iterationsResponse.data || []);
      } catch (err) {
        console.error("Error loading campaign iterations:", err);
        setError("Error");
      } finally {
        setLoading(false);
      }
    };

    loadCampaignIterations();
  }, [campaign.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingIndicator isFullScreen={false} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 rounded-md">
        <p className="text-sm text-red-600">
          Error loading iterations: {error}
        </p>
      </div>
    );
  }

  if (iterationsData.length === 0) {
    return (
      <div className="p-4 bg-gray-50 rounded-md">
        <p className="text-sm text-gray-500">No iteration data available</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <svg
            className="w-5 h-5 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
          Campaign Iterations
        </h3>
        <button
          className={`text-sm flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors ${
            showBreakdown
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
          onClick={() => setShowBreakdown(!showBreakdown)}
        >
          <Users className="w-4 h-4" />
          {showBreakdown ? "Hide Breakdown" : "Show Breakdown"}
        </button>
      </div>

      <div className="overflow-x-auto [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
        <div className="flex items-start gap-6 pb-2">
          {iterationsData.map((iteration) => {
            return (
              <div className="flex-shrink-0" key={iteration.id}>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="text-sm font-semibold mb-1 flex items-center gap-2">
                        <span className="text-blue-600">
                          Iteration {iteration.iterationNumber}
                        </span>
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(iteration.campaignIterationStatus.name)}`}
                        >
                          {iteration.campaignIterationStatus.name}
                        </span>
                      </h4>
                      <p className="text-xs text-gray-600">
                        Weeks {iteration.weeks}
                      </p>
                      <p className="text-xs text-gray-500">
                        {format(new Date(iteration.startDate), "yyyy-MM-dd")} -{" "}
                        {format(new Date(iteration.endDate), "yyyy-MM-dd")}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-600">Total Prospects</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {iteration.totalProspectsCount.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  {iteration.campaignIterationWeeks.map((week) => {
                    if (!week.isMailingDropWeek) {
                      return (
                        <div
                          className="flex-shrink-0 w-64 bg-white border-2 border-dashed border-gray-300 rounded-md p-8 shadow-sm"
                          key={week.id}
                        >
                          <div className="flex flex-col items-center justify-center text-center">
                            <h5 className="text-sm font-semibold text-gray-400 mb-4">
                              Week {week.weekNumber}
                            </h5>
                            <svg
                              className="w-12 h-12 text-gray-300 mb-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                              />
                            </svg>
                            <p className="text-xs text-gray-400">
                              No mail drop
                            </p>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        className="flex-shrink-0 w-64 bg-white border border-gray-200 rounded-md p-4 shadow-sm"
                        key={week.id}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <svg
                              className="w-4 h-4 text-gray-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                              />
                            </svg>
                            <h5 className="text-sm font-semibold text-gray-900">
                              Week {week.weekNumber}
                            </h5>
                          </div>
                          {week.campaignIterationWeekStatus.name && (
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(week.campaignIterationWeekStatus.name)}`}
                            >
                              {week.campaignIterationWeekStatus.name}
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-gray-500 mb-3">
                          {format(new Date(week.startDate), "yyyy-MM-dd")} -{" "}
                          {format(new Date(week.endDate), "yyyy-MM-dd")}
                        </p>

                        {week.postageCost !== null && (
                          <div className="bg-green-50 rounded-md px-3 py-2 mb-3">
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-green-700">
                                Postage Cost:
                              </p>
                              <p className="text-xs font-semibold text-green-700">
                                {formatCurrency(week.postageCost)}
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="mb-3">
                          <div className="flex items-center gap-1 mb-2">
                            <svg
                              className="w-3.5 h-3.5 text-gray-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                              />
                              <path
                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                              />
                            </svg>
                            <p className="text-xs font-medium text-gray-700">
                              Zip Codes
                            </p>
                          </div>
                          <div className="space-y-2">
                            {Object.entries(week.zipCodes).map(
                              ([zip, zipData]) => (
                                <div
                                  className="bg-gray-50 rounded px-2 py-1.5"
                                  key={zip}
                                >
                                  <div className="flex justify-between text-xs">
                                    <span className="text-gray-700">{zip}</span>
                                    <span className="font-medium text-gray-900">
                                      {zipData.count.toLocaleString()}
                                    </span>
                                  </div>
                                  {showBreakdown && (
                                    <div className="ml-4 mt-1 space-y-0.5">
                                      <div className="flex justify-between text-xs">
                                        <span className="text-gray-600">
                                          New Prospects:
                                        </span>
                                        <span className="font-semibold text-green-600">
                                          {zipData.newProspects.toLocaleString()}
                                        </span>
                                      </div>
                                      <div className="flex justify-between text-xs">
                                        <span className="text-gray-600">
                                          Existing Customers:
                                        </span>
                                        <span className="font-semibold text-blue-600">
                                          {zipData.existingCustomers.toLocaleString()}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ),
                            )}
                          </div>
                        </div>

                        <div className="py-3 border-t border-gray-200 space-y-1">
                          <div
                            className={`flex justify-between items-center ${showBreakdown ? "pb-1 border-b border-gray-200" : ""}`}
                          >
                            <span className="text-xs font-semibold text-gray-600">
                              Total:
                            </span>
                            <span className="text-xs font-semibold text-blue-600">
                              {week.totalProspects.toLocaleString()}
                            </span>
                          </div>

                          {showBreakdown && (
                            <div className="pt-1 space-y-1">
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-gray-600">
                                  New Prospects:
                                </span>
                                <span className="text-xs font-semibold text-green-600">
                                  {week.totalNewProspects.toLocaleString()}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-xs text-gray-600">
                                  Existing Customers:
                                </span>
                                <span className="text-xs font-semibold text-blue-600">
                                  {week.totalExistingCustomers.toLocaleString()}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CampaignStatusExpandedRow;
